import { useEffect } from "react";

/** Lightweight SEO hook — sets document.title, meta description, canonical,
 *  OpenGraph tags, and optional JSON-LD structured data per route.
 *  Cleans up to avoid pollution between routes.
 */
export function useSeo({ title, description, canonical, image, jsonLd, type = "website" }) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const tags = [];
    const setMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      } else {
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      }
      tags.push({ el, created });
    };

    if (description) {
      setMeta('meta[name="description"]', { name: "description", content: description });
      setMeta('meta[property="og:description"]', { property: "og:description", content: description });
      setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    }
    if (title) {
      setMeta('meta[property="og:title"]', { property: "og:title", content: title });
      setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    }
    setMeta('meta[property="og:type"]', { property: "og:type", content: type });
    setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    if (image) {
      setMeta('meta[property="og:image"]', { property: "og:image", content: image });
      setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: image });
    }

    let canonEl = null;
    if (canonical) {
      canonEl = document.head.querySelector('link[rel="canonical"]');
      const created = !canonEl;
      if (!canonEl) {
        canonEl = document.createElement("link");
        canonEl.setAttribute("rel", "canonical");
        document.head.appendChild(canonEl);
      }
      canonEl.setAttribute("href", canonical);
      canonEl.__seo_created = created;
    }

    let ldEl = null;
    if (jsonLd) {
      ldEl = document.createElement("script");
      ldEl.type = "application/ld+json";
      ldEl.text = JSON.stringify(jsonLd);
      ldEl.setAttribute("data-seo-hook", "true");
      document.head.appendChild(ldEl);
    }

    return () => {
      document.title = prevTitle;
      // Remove any meta tags we created; leave existing ones alone (they'll be re-set by next page)
      if (ldEl && ldEl.parentNode) ldEl.parentNode.removeChild(ldEl);
      if (canonEl && canonEl.__seo_created && canonEl.parentNode) canonEl.parentNode.removeChild(canonEl);
    };
  }, [title, description, canonical, image, jsonLd, type]);
}
